import { gql } from "lib/apolloClient";
import {
  learningCourseFeedFragment,
  gradeFragment,
  learningCourseListItem,
} from "./LearningCoursesFragments";

export const getLearningCoursesFeedQuery = gql`
  query getLearningCoursesFeed(
    $curriculumProgramId: ID!
    $curriculumType: ENTITY_TYPE_ENUM!
    $orderByDirection: ORDER_BY_DIRECTION
    $orderBy: ORDER_BY_ENUM
    $collaboratorFilters: LearningCourseCollaboratorFilters
    $classesFilters: LearningCourseClassFilter
    $filters: LearningCourseFilters
    $after: String
    $gradeFilters: LearningCourseGradeFilters
  ) {
    node(id: $curriculumProgramId, type: $curriculumType) {
      id
      ... on IbPypCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on IbMypCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on UBDCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on IbDpCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on GenericCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${learningCourseFeedFragment}
`;

export const getLearningCoursesCountQuery = gql`
  query getLearningCountCourses(
    $curriculumProgramId: ID!
    $curriculumType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $curriculumProgramId, type: $curriculumType) {
      id
      ... on IbPypCurriculumProgram {
        id
        learningCourse {
          count {
            totalCount
            teacherCourseCount
            bluePrintCourseCount
          }
        }
      }

      ... on IbMypCurriculumProgram {
        id
        learningCourse {
          count {
            totalCount
            teacherCourseCount
            bluePrintCourseCount
          }
        }
      }

      ... on UBDCurriculumProgram {
        id
        learningCourse {
          count {
            totalCount
            teacherCourseCount
            bluePrintCourseCount
          }
        }
      }

      ... on IbDpCurriculumProgram {
        id
        learningCourse {
          count {
            totalCount
            teacherCourseCount
            bluePrintCourseCount
          }
        }
      }

      ... on GenericCurriculumProgram {
        id
        learningCourse {
          count {
            totalCount
            teacherCourseCount
            bluePrintCourseCount
          }
        }
      }
    }
  }
`;

export const getClassesCountQuery = gql`
  query getClassesCount($organizationId: ID!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(first: 1000) {
          totalCount
        }
      }
    }
  }
`;

export const getGradingPeriodsAndLevelsQuery = gql`
  query getGradingPeriodsAndLevels(
    $id: ID!
    $curriculumIds: [ID!]
    $gradingPeriodsFilters: TermFilters!
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumIds) {
          ... on IbDpCurriculumProgram {
            levels {
              id
              label
              type
            }
          }
          gradingPeriods(filters: $gradingPeriodsFilters) {
            edges {
              node {
                id
                label
                startDate
                endDate
                academicYear {
                  id
                }
                curriculumProgram {
                  id
                  nodeInterfaceType
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getDpSubjectVariantsQueryV2 = gql`
  query getOrganizationSubjectVariants($id: ID!) {
    node(id: $id, type: IB_DP_CURRICULUM_PROGRAM) {
      ... on IbDpCurriculumProgram {
        id
        subjectVariants(filters: { archivalState: ALL }) {
          subject {
            name
            id
            icon
            iconType
            type
            ibOfferedSubject {
              id
              contentSubject {
                id
                assessmentYearTitle
                assessmentYearEndsOn
              }
            }
            isArchived
            associatedLevels {
              id
              label
              type
            }
            flatVariants(filters: { archivalState: ALL }) {
              level {
                id
                label
              }
              responseLanguage {
                id
                label
              }
              isArchived
            }
            subjectGroup {
              id
              globalSubjectGroup {
                id
              }
            }
          }
          subjectGroup {
            id
            name
            type
            globalSubjectGroup {
              id
              name
              type
            }
            displaySequence
          }
        }
      }
    }
  }
`;

export const getLearningCourseDetailsQuery = gql`
  query getLearningCourseDetails(
    $id: ID!
    $gradeFilters: LearningCourseGradeFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        title
        displayName
        code
        courseSubjectType
        academicCourse {
          id
        }
        curriculumProgram {
          id
        }
        primaryGrade(filters: $gradeFilters) {
          ...gradeItem
        }
        grades(filters: $gradeFilters) {
          ...gradeItem
        }
        displayMetadata
        includeAllGradingPeriods
        subjects {
          id
          name
          isArchived
          grades {
            id
            name
          }
          subjectGroup {
            id
            name
            type
            globalSubjectGroup {
              id
              constants
            }
          }
          associatedLevels {
            id
            label
            type
          }
          isArchived
        }
        associatedLevels {
          id
          label
          type
        }
        associatedLevelWiseStudentCount {
          associatedLevel {
            id
          }
          studentCount
        }
        academicYears {
          id
          startDate
          endDate
        }
        responseLanguage {
          id
          label
          type
        }
        gradingPeriods {
          edges {
            node {
              id
              label
              startDate
              endDate
              academicYear {
                id
                startDate
                endDate
              }
            }
          }
        }
        subjectGroup {
          id
          name
        }
        subjectLevelMap {
          subject {
            id
          }
          associatedLevels {
            id
          }
          associatedLevelWiseStudentCount {
            associatedLevel {
              id
            }
            studentCount
          }
        }
        bluePrintCourse {
          id
          title
          displayMetadata
        }
        availableBlueprintCourses(input: { highlightDisabled: true }) {
          id
          title
          displayMetadata
          childCourses {
            id
          }
          grades {
            id
          }
          subjects {
            id
          }
          gradingPeriods {
            edges {
              node {
                id
                label
                startDate
                endDate
                academicYear {
                  id
                  startDate
                  endDate
                }
              }
            }
          }
          responseLanguage {
            id
          }
          academicYears {
            id
          }
          associatedLevels {
            id
            label
            type
          }
          subjectLevelMap {
            subject {
              id
            }
            associatedLevels {
              id
            }
          }
          isLearningCourseAvailableForAssociation
        }
        isCourseEmpty
      }
    }
  }
  ${gradeFragment}
`;

export const getBluePrintCourseListQuery = gql`
  query getLearningCoursesList(
    $curriculumProgramId: ID!
    $curriculumType: ENTITY_TYPE_ENUM!
    $filters: LearningCourseFilters
    $academicCourseStudentsFilters: AcademicCourseStudentFilters
  ) {
    node(id: $curriculumProgramId, type: $curriculumType) {
      id
      ... on IbPypCurriculumProgram {
        id
        learningCourse(filters: $filters) {
          edges {
            node {
              ...learningCourseListItem
            }
          }
        }
      }
      ... on IbMypCurriculumProgram {
        id
        learningCourse(filters: $filters) {
          edges {
            node {
              ...learningCourseListItem
            }
          }
        }
      }
      ... on UBDCurriculumProgram {
        id
        learningCourse(filters: $filters) {
          edges {
            node {
              ...learningCourseListItem
            }
          }
        }
      }
      ... on IbDpCurriculumProgram {
        id
        learningCourse(filters: $filters) {
          edges {
            node {
              ...learningCourseListItem
            }
          }
        }
      }
      ... on GenericCurriculumProgram {
        id
        learningCourse(filters: $filters) {
          edges {
            node {
              ...learningCourseListItem
            }
          }
        }
      }
    }
  }
  ${learningCourseListItem}
`;
